import React, { useEffect, useState } from 'react';
import { calendar as calApi, auth as authApi } from '@/utils/api';
import { Calendar as CalIcon, Link as LinkIcon, Check, X, Save, Copy, ExternalLink, Ban, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' }, { value: 7, label: 'Sun' },
];

export default function CalendarPage() {
  const [status, setStatus] = useState({ connected: false });
  const [rules, setRules] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [overrideForm, setOverrideForm] = useState({ date: '', kind: 'block', start_hour: 9, end_hour: 17, reason: '' });

  const reload = () => {
    calApi.status().then(r => setStatus(r.data)).catch(() => {});
    calApi.getRules().then(r => setRules(r.data)).catch(() => {});
    calApi.bookings().then(r => setBookings(r.data)).catch(() => {});
    authApi.me().then(r => setMe(r.data)).catch(() => {});
    calApi.overrides().then(r => setOverrides(r.data)).catch(() => {});
  };

  useEffect(() => { reload(); }, []);

  const connectGoogle = async () => {
    try {
      const r = await calApi.oauthStart();
      window.location.href = r.data.auth_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Google OAuth not configured'); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Calendar?')) return;
    await calApi.disconnect();
    reload();
    toast.success('Disconnected');
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      await calApi.putRules(rules);
      toast.success('Availability saved');
    } catch (e) { toast.error('Failed'); } finally { setSaving(false); }
  };

  const publicUrl = me ? `${window.location.origin}/book/${me.id}` : '';

  return (
    <div className="p-6 animate-fade-in" data-testid="calendar-page">
      <h1 className="text-xl font-black mb-4" style={{ color: 'var(--ink)' }}>Calendar & Scheduling</h1>

      {/* Google status */}
      <div className="card-base p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CalIcon size={20} style={{ color: '#006080' }} />
            <div>
              <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Google Calendar</p>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                {status.connected ? 'Connected — bookings sync with Google Meet' : 'Not connected — bookings saved locally only'}
              </p>
            </div>
          </div>
          {status.connected ? (
            <button className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ color: '#B34A3A', border: '1px solid #B34A3A' }} onClick={disconnect} data-testid="disconnect-google-button">
              <X size={12} /> Disconnect
            </button>
          ) : (
            <button className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5" onClick={connectGoogle} data-testid="connect-google-button">
              <LinkIcon size={12} /> Connect Google
            </button>
          )}
        </div>
      </div>

      {/* Availability rules */}
      {rules && (
        <div className="card-base p-4 mb-4">
          <h2 className="font-bold text-sm mb-3" style={{ color: 'var(--ink)' }}>Availability rules</h2>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Timezone</label>
              <input className="input-agency text-sm" value={rules.timezone} onChange={e => setRules({ ...rules, timezone: e.target.value })} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Slot duration (minutes)</label>
              <input className="input-agency text-sm" type="number" value={rules.slot_duration_minutes} onChange={e => setRules({ ...rules, slot_duration_minutes: Number(e.target.value) })} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Start hour (0-23)</label>
              <input className="input-agency text-sm" type="number" min="0" max="23" value={rules.start_hour} onChange={e => setRules({ ...rules, start_hour: Number(e.target.value) })} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>End hour (0-23)</label>
              <input className="input-agency text-sm" type="number" min="0" max="23" value={rules.end_hour} onChange={e => setRules({ ...rules, end_hour: Number(e.target.value) })} />
            </div>
          </div>
          <div className="mb-3">
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Working days</label>
            <div className="flex gap-2 flex-wrap">
              {DAYS.map(d => (
                <button key={d.value} type="button" className="text-xs px-3 py-1.5 rounded-btn font-semibold" style={{ background: rules.working_days.includes(d.value) ? '#006080' : 'var(--bg)', color: rules.working_days.includes(d.value) ? 'white' : 'var(--muted)', border: '1px solid var(--border)' }} onClick={() => setRules(r => ({ ...r, working_days: r.working_days.includes(d.value) ? r.working_days.filter(x => x !== d.value) : [...r.working_days, d.value] }))}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>
          <button className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" disabled={saving} onClick={saveRules} data-testid="save-availability-button">
            <Save size={12} /> {saving ? 'Saving…' : 'Save Rules'}
          </button>

          {publicUrl && (
            <div className="mt-4 p-3 rounded-btn flex items-center gap-3" style={{ background: 'var(--bg)' }}>
              <p className="text-xs flex-shrink-0" style={{ color: 'var(--muted)' }}>Public booking link:</p>
              <code className="text-xs flex-1 font-mono truncate" style={{ color: 'var(--ink)' }}>{publicUrl}</code>
              <button className="text-xs flex items-center gap-1" style={{ color: '#006080' }} onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Copied'); }}>
                <Copy size={12} /> Copy
              </button>
              <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1" style={{ color: '#006080' }}>
                <ExternalLink size={12} /> Open
              </a>
            </div>
          )}
        </div>
      )}

      {/* Bookings list */}
      <div className="card-base p-4 mb-4">
        <h2 className="font-bold text-sm mb-3" style={{ color: 'var(--ink)' }}>Upcoming bookings</h2>
        {bookings.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--muted)' }}>No bookings yet.</p>
        ) : (
          <div className="space-y-2">
            {bookings.map(b => (
              <div key={b.id} className="flex items-center justify-between p-2 rounded-btn" style={{ background: 'var(--bg)' }} data-testid={`booking-${b.id}`}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{b.title} · {b.attendee_name}</p>
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>{new Date(b.start).toLocaleString()} · {b.attendee_email}</p>
                </div>
                {b.meet_link && (
                  <a href={b.meet_link} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1" style={{ color: '#006080' }}>
                    <ExternalLink size={12} /> Meet
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Availability overrides */}
      <div className="card-base p-4">
        <div className="flex items-center gap-2 mb-3">
          <Ban size={16} style={{ color: '#B34A3A' }} />
          <h2 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Blocked dates & custom hours</h2>
        </div>
        <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>Override default availability on specific dates — block fully, or run custom hours (e.g. half-day).</p>

        <div className="grid grid-cols-12 gap-2 mb-3">
          <input className="input-agency text-sm col-span-3" type="date" value={overrideForm.date} onChange={e => setOverrideForm({...overrideForm, date: e.target.value})} data-testid="override-date" />
          <select className="input-agency text-sm col-span-3" value={overrideForm.kind} onChange={e => setOverrideForm({...overrideForm, kind: e.target.value})}>
            <option value="block">Block all day</option>
            <option value="custom_hours">Custom hours</option>
          </select>
          {overrideForm.kind === 'custom_hours' && (
            <>
              <input className="input-agency text-sm col-span-1" type="number" min="0" max="23" placeholder="From" value={overrideForm.start_hour} onChange={e => setOverrideForm({...overrideForm, start_hour: Number(e.target.value)})} />
              <input className="input-agency text-sm col-span-1" type="number" min="0" max="23" placeholder="To" value={overrideForm.end_hour} onChange={e => setOverrideForm({...overrideForm, end_hour: Number(e.target.value)})} />
            </>
          )}
          <input className={`input-agency text-sm ${overrideForm.kind === 'custom_hours' ? 'col-span-3' : 'col-span-5'}`} placeholder="Reason (optional)" value={overrideForm.reason} onChange={e => setOverrideForm({...overrideForm, reason: e.target.value})} />
          <button className="btn-primary text-xs col-span-1 flex items-center justify-center" onClick={async () => {
            if (!overrideForm.date) return toast.error('Pick a date');
            try {
              await calApi.addOverride(overrideForm);
              toast.success('Override added');
              setOverrideForm({ date: '', kind: 'block', start_hour: 9, end_hour: 17, reason: '' });
              reload();
            } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
          }} data-testid="add-override-button">
            <Plus size={13} />
          </button>
        </div>

        {overrides.length === 0 ? (
          <p className="text-xs text-center py-3" style={{ color: 'var(--muted)' }}>No overrides set.</p>
        ) : (
          <div className="space-y-1">
            {overrides.map(o => (
              <div key={o.id} className="flex items-center justify-between p-2 rounded-btn" style={{ background: 'var(--bg)' }} data-testid={`override-${o.id}`}>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono font-semibold" style={{ color: 'var(--ink)' }}>{o.date}</span>
                  {o.kind === 'block' ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: '#B34A3A15', color: '#B34A3A' }}>BLOCKED</span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: '#E6F1F4', color: '#006080' }}>{o.start_hour}:00 – {o.end_hour}:00</span>
                  )}
                  {o.reason && <span className="text-xs" style={{ color: 'var(--muted)' }}>{o.reason}</span>}
                </div>
                <button onClick={async () => { await calApi.deleteOverride(o.id); reload(); }} className="p-1" style={{ color: '#B34A3A' }} data-testid={`delete-override-${o.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
